
import React from 'react';
import { Clock, Shield, FileText, Database, Users, AlertTriangle, Zap, Radio, Cloud, ShoppingBag, MapPin } from 'lucide-react';
import Navbar from '@/components/Navbar';
import PageTransition from '@/components/PageTransition';
import { AnimatedButton } from '@/components/AnimatedButton';
import { Link } from 'react-router-dom';
import FeatureCard from '@/components/FeatureCard';
import Footer from '@/components/Footer';
import PlanTypesSection from '@/components/homepage/PlanTypesSection';
import WorkflowSection from '@/components/features/WorkflowSection';

const Features = () => {
  const features = [
    {
      icon: Shield,
      title: "Personalized Planning",
      description: "Get a custom emergency plan created through AI-powered conversational interviews that adapt to your unique family dynamics, home environment, and medical needs."
    },
    {
      icon: Clock,
      title: "Rapid Response",
      description: "Access your emergency plan instantly with Quick Action Cards that provide critical information with a single tap when seconds matter."
    },
    {
      icon: FileText,
      title: "Comprehensive Guidance",
      description: "Helpful information delivered in simple, actionable steps for emergency scenarios relevant to your specific situation."
    },
    {
      icon: Database,
      title: "Secure Storage",
      description: "Your sensitive information protected with enterprise-grade encryption and strong data security measures to ensure your privacy."
    },
    {
      icon: Users,
      title: "Family Sharing",
      description: "Assign specific roles and responsibilities to family members with customized instructions based on their capabilities and needs."
    },
    {
      icon: AlertTriangle,
      title: "Risk Assessment",
      description: "Upload images of your property to help identify potential hazards that might be missed with traditional assessment methods."
    },
    {
      icon: Zap,
      title: "Quick Action Cards",
      description: "Prioritized emergency actions with visual guides that anyone can follow under stress, accessible with just one tap."
    },
    {
      icon: Radio,
      title: "Downloadable Reports",
      description: "Download your emergency plans as PDF documents for offline reference when internet access is unavailable during emergencies."
    },
    {
      icon: Cloud,
      title: "Cloud Backup",
      description: "Your emergency plans are securely stored online, allowing you to access them from any device with a web browser."
    },
    {
      icon: ShoppingBag,
      title: "Emergency Supplies",
      description: "Generate a customized shopping list of emergency supplies based on your plan, with optional Amazon integration for easy purchasing."
    },
    {
      icon: MapPin,
      title: "Local Resource Network",
      description: "Connect with local emergency service providers and resources specific to your location and identified needs."
    }
  ];

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-28 md:pt-32 pb-16">
          <div className="container px-4 md:px-6">
            <div className="text-center max-w-[800px] mx-auto mb-12 opacity-0 animate-fade-up pt-[100px]">
              <h1 className="hero-text text-balance mb-6">
                Features Designed to Keep You <span className="text-primary">Safe</span>
              </h1>
              <p className="body-lg text-muted-foreground mb-8 mx-auto max-w-[650px]">
                Our comprehensive emergency planning platform provides all the tools you 
                need to prepare for and respond to emergencies effectively.
              </p>
            </div>
          </div>
        </section>
        
        {/* Workflow Section - Added New Section */}
        <WorkflowSection />
        
        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <FeatureCard 
                  key={index}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  index={index}
                />
              ))}
            </div>
          </div>
        </section>
        
        {/* Plan Types Section */}
        <PlanTypesSection />
        
        {/* CTA Section */}
        <section className="py-20">
          <div className="container px-4 md:px-6">
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 md:p-10 relative overflow-hidden">
              <div className="relative z-10 max-w-[600px] mx-auto text-center opacity-0 animate-fade-up">
                <h2 className="heading-lg mb-4">Ready to start your emergency planning?</h2>
                <p className="body-base text-muted-foreground mb-8">
                  Join thousands of families and businesses who trust EmergencyPlan 
                  for their safety and preparedness needs.
                </p>
                <Link to="/signup">
                  <AnimatedButton size="lg">
                    Get Started Today
                  </AnimatedButton>
                </Link>
              </div>
            </div>
          </div>
        </section>
        
        <Footer />
      </div>
    </PageTransition>
  );
};

export default Features;
